import axios from 'axios';
import { localizeServerMessage } from '../i18n';
import {
  expireAdminSession,
  getAdminToken,
  handleAdminUnauthorized,
  isAdminTokenValid,
  isPublicAdminAuthRequest,
} from '../auth/adminSession';

// CRA: 환경변수는 REACT_APP_* 만 빌드 시 주입
// 백엔드가 /api 프리픽스를 안 쓰므로 기본값은 ''(없음).
// 나중에 프록시를 붙이면 .env에 REACT_APP_API_BASE=/api 만 지정하면 됨.
// [PATCH][ANCHOR:ADMIN_CLIENT:AXIOS_BASEURL_TO_ADMIN]
// ✅ 어드민 클라이언트는 기본적으로 "어드민 서버"를 바라보게 고정
//    - 권장: admin-client/.env 에 REACT_APP_API_BASE=http://localhost:3100 설정
//    - 누락 시(실수 방지): 개발환경에서만 3100으로 폴백
const __rawBase =
  (process.env.REACT_APP_ADMIN_API_BASE_URL ?? '').trim() ||
  (process.env.REACT_APP_ADMIN_API_BASE ?? '').trim() ||
  (process.env.REACT_APP_API_BASE_URL ?? '').trim() ||
  (process.env.REACT_APP_API_BASE ?? '').trim() ||
  (process.env.NODE_ENV === 'development' ? 'http://localhost:3100' : '');

if (!__rawBase) {
  // eslint-disable-next-line no-console
  console.warn('[ADMIN_CLIENT] REACT_APP_API_BASE is missing. baseURL is empty; requests may hit wrong origin.');
}

const baseURL = __rawBase.replace(/\/$/, '');

// Keep legacy direct imports of `axios` safe if any page/component still imports axios from the package.
axios.defaults.baseURL = baseURL;
axios.defaults.withCredentials = true;

const instance = axios.create({
  baseURL,
  withCredentials: true,
});

instance.interceptors.request.use((config) => {
  const requestUrl = String(config?.url || '');

  // 로그인/회원가입은 기존 만료 토큰과 완전히 분리한다.
  if (isPublicAdminAuthRequest(requestUrl)) {
    if (config.headers?.Authorization) delete config.headers.Authorization;
    return config;
  }

  const token = getAdminToken();
  if (!token) return config;

  // 서버 요청 전에 만료를 발견하면 만료 토큰을 전송하지 않고 세션을 정리한다.
  if (!isAdminTokenValid(token)) {
    expireAdminSession();
    return Promise.reject(new axios.CanceledError('Admin session expired'));
  }

  config.headers = config.headers || {};
  config.headers.Authorization = `Bearer ${token}`;
  return config;
});

function localizeResponseMessages(data) {
  if (!data || typeof data !== 'object') return data;
  const next = Array.isArray(data) ? data : { ...data };
  if (!Array.isArray(next)) {
    if (typeof next.message === 'string') {
      next.message = localizeServerMessage(next.message, '서버 요청을 처리하지 못했습니다.');
    }
    if (typeof next.warning === 'string') {
      next.warning = localizeServerMessage(next.warning, '요청은 처리되었지만 확인이 필요한 항목이 있습니다.');
    }
    if (next.cashier && typeof next.cashier === 'object' && typeof next.cashier.message === 'string') {
      next.cashier = {
        ...next.cashier,
        message: localizeServerMessage(next.cashier.message, '결제 요청을 처리하지 못했습니다.'),
      };
    }
    if (next.resolved && typeof next.resolved === 'object' && typeof next.resolved.message === 'string') {
      next.resolved = {
        ...next.resolved,
        message: localizeServerMessage(next.resolved.message, '처리가 완료되었습니다.'),
      };
    }
  }
  return next;
}

instance.interceptors.response.use(
  (response) => {
    if (response && response.data) response.data = localizeResponseMessages(response.data);
    return response;
  },
  (error) => {
    const status = error?.response?.status;
    const requestUrl = error?.config?.url || '';

    // 보호 API에서 401을 받으면 만료/무효 세션을 즉시 폐기하고 로그인 화면으로 복귀한다.
    handleAdminUnauthorized(status, requestUrl, error?.response?.data);

    if (error?.response?.data) {
      error.response.data = localizeResponseMessages(error.response.data);
    }
    return Promise.reject(error);
  }
);

export default instance;
